import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Grid, Box, Typography, Card, Badge, Chip, Divider } from '@mui/material';
import { ReactComponent as FilterAltOutlinedIcon } from '../../../static/icons/filter.svg';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import SolidGauge from 'highcharts/modules/solid-gauge';
import './CallCenterWallboard.css';
import FilterDrawerCaseWise from '../../FilterDrawer/FilterDrawerCaseWise';
import { WIDGET_REFRESH_INTERVAL } from '../../../utils/constants';
import { getLastFetchedDetailFromBrowser } from '../../../services/application-service';
import { getWallboardCallMetric, getWallboardDashboardMetric } from '../../../services/api-service';
import { secondsToMinSec } from '../../../utils/util';
import ThemeSwitcher from '../../ModeSwitch/ThemeSwitcher';
import useThemeMode from '../../ModeSwitch/useThemeMode';
import useElementDimensions from '../../../hooks/useElementDimensions';

// Initialize Highcharts modules
HighchartsMore(Highcharts);
SolidGauge(Highcharts);

interface KPICardProps {
    title: string;
    dayValue?: string | number;
    intervalValue?: string | number;
    value?: string | number;
    backgroundColor: string;
    comboCard?: boolean;
    shouldBlink?: boolean;
    isThemeMode?: boolean;
    titleValue?: string | number | null;
}

interface SpeedometerComboCardProps {
    dayValue: number | string;
    intervalValue: number | string;
    type: 'sla' | 'abandoned';
    isDarkMode: boolean;
    title: string;
}

interface SpeedometerProps {
    percentage: number;
    type: 'sla' | 'abandoned';
    label: string;
    isThemeMode?: boolean;
    height: number;
    width: number;
}

const KPICard: React.FC<KPICardProps> = ({ title, dayValue, intervalValue, value, backgroundColor, comboCard = false, shouldBlink = false, isThemeMode = false, titleValue = null }) => {
    // Dynamic color logic for value text based on title and theme
    const getValueColor = () => {
        if (title === 'LONG CALL') {
            const numValue = typeof value === 'number' ? value : parseInt(value?.toString() || '0');
            if (numValue <= 1) return '#6b7280'; // Grey for 0-1 calls
            if (numValue <= 5) return '#f59e0b'; // Amber for 2-5 calls with blink
            return '#ef4444'; // Red for ≥6 calls with blink
        }
        if (title === 'Calls Waiting') {
            const numValue = typeof value === 'number' ? value : parseInt(value?.toString() || '0');
            if (numValue > 2) return '#ef4444'; // Red when > 2
            return isThemeMode ? "#FFFFFF" : "#000000"; // Normal color when ≤ 2
        }
        return isThemeMode ? "#FFFFFF" : "#000000";
    };

    const isAHT = useMemo(() => title === 'AHT', [title]);

    return (
        <Card className="kpi-card" sx={{ backgroundColor, flexDirection: 'column', border: isThemeMode ? 'none' : '1px solid #e5e7eb' }}>
            {
                titleValue !== null && titleValue !== undefined ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%' }}>
                    <Typography className="kpi-title" sx={{
                        color: isThemeMode ? '#E2E8F0' : '#1F2937',
                        fontSize: 'clamp(1.5rem, 0.25rem + 1.6vw, 3rem)',
                        fontWeight: '600 !important',
                        letterSpacing: '.02em',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                    }}>
                        {title}
                    </Typography>
                    <Typography className={`kpi-value ${shouldBlink ? 'blinking' : ''}`} sx={{
                        color: getValueColor(),
                        fontSize: 'clamp(1.5rem, 2.5vw, 4rem);',
                        fontWeight: 'bold',
                    }}>
                        {titleValue}
                    </Typography>
                </Box>
                    :
                    <>
                        <Typography className="kpi-title" sx={{
                            color: isThemeMode ? '#E2E8F0' : '#1F2937',
                            fontSize: '1.9vw',
                            fontWeight: '600 !important',
                            letterSpacing: '.02em'
                        }}>
                            {title}
                        </Typography>
                        <Divider />
                        {
                            !comboCard ? (
                                <Typography className={`kpi-value ${shouldBlink ? 'blinking' : ''}`} sx={{
                                    color: getValueColor(),
                                    fontSize: '4vw',
                                    fontWeight: '600',
                                    width: '100%',
                                    textAlign: 'center',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {value}
                                </Typography>
                            ) : (<div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                alignItems: 'center',
                                justifyContent: 'space-around',
                                height: '70%',
                                width: '100%'
                            }}>
                                <div className="kpi-value-container" style={{ width: '100%', justifyContent: 'space-between', gap: '10px' }}>
                                    <Typography sx={{
                                        color: isThemeMode ? '#94a3b8' : '#6b7280',
                                        fontSize: '1.4vw',
                                        fontWeight: 500,
                                        lineHeight: 'normal',
                                        textAlign: 'left',
                                        letterSpacing: '.01em',
                                        textTransform: 'uppercase',
                                    }}>
                                        DAY
                                    </Typography>
                                    <Typography sx={{
                                        color: isThemeMode ? '#F8FAFC' : '#111827',
                                        fontSize: '2.5vw',
                                        fontWeight: '700',
                                        lineHeight: 'normal',
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums'
                                    }}>{dayValue}</Typography>
                                </div>
                                <div className="kpi-value-container" style={{
                                    width: '100%', justifyContent: 'space-between', gap: '10px',
                                    ...(isAHT ? {
                                        overflowX: 'auto',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%'
                                    } : {})
                                }}>
                                    <Typography sx={{
                                        color: isThemeMode ? '#94a3b8' : '#6b7280',
                                        fontSize: '1.4vw',
                                        fontWeight: 500,
                                        lineHeight: 'normal',
                                        textAlign: 'left',
                                        letterSpacing: '.01em',
                                        textTransform: 'uppercase',
                                        // wordBreak: 'break-all'
                                    }}>
                                        INTERVAL
                                    </Typography>
                                    <Typography sx={{
                                        color: isThemeMode ? '#F8FAFC' : '#111827',
                                        fontSize: '2.5vw',
                                        fontWeight: '700',
                                        lineHeight: 'normal',
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums'
                                    }}>{intervalValue}</Typography>
                                </div>
                            </div>
                            )
                        }
                    </>
            }
        </Card>
    );
};

// Speedometer component
const Speedometer: React.FC<SpeedometerProps> = ({ percentage, type, label, isThemeMode = false, height, width }) => {
    const chartElementRef = useRef<any>(null);
    useEffect(() => {
        const chart = chartElementRef?.current?.chart;
        if (!chart) return;
        const raf = requestAnimationFrame(() => {
            try {
                chart.update({
                    chart: { height, width },
                    pane: { center: ['50%', '50%'] },
                }, false, true, false);
                chart.reflow();
                chart.redraw(true);
            } catch (error) {
                console.log('Error updating chart dimensions', error);
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [height, width]);

    const getSpeedometerColor = () => {
        if (type === 'sla') {
            if (percentage >= 80) return '#22c55e'; // Green
            if (percentage >= 70) return '#f59e0b'; // Amber
            return '#ef4444'; // Red
        } else { // abandoned
            if (percentage <= 5) return '#22c55e'; // Green
            if (percentage <= 9.99) return '#f59e0b'; // Amber
            return '#ef4444'; // Red
        }
    };

    const calculateOptimalFontSize = () => {
        try {
            const text = `${percentage}%`;
            const minDimension = Math.min(height, width);
            const innerRadius = 0.93;

            // Available space with safety margins
            const availableWidth = minDimension * innerRadius * 0.85; // 85% for horizontal padding
            const availableHeight = minDimension * innerRadius * 0.5;  // 50% for vertical space

            // Create a canvas to measure actual text dimensions
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                // Fallback to estimation if canvas not available
                const estimatedSize = Math.min(availableHeight * 0.8, availableWidth / (text.length * 0.6));
                return Math.max(8, Math.floor(estimatedSize));
            }

            // Binary search for optimal font size
            let minSize = 8;
            let maxSize = Math.min(availableHeight, availableWidth * 0.8);
            let optimalSize = minSize;

            for (let iterations = 0; iterations < 20 && maxSize - minSize > 0.5; iterations++) {
                const testSize = (minSize + maxSize) / 2;

                // Set font and measure text
                ctx.font = `bold ${testSize}px Arial, sans-serif`;
                const textMetrics = ctx.measureText(text);
                const textWidth = textMetrics.width;
                const textHeight = testSize; // Approximate height as font size

                // Check if text fits within available space
                if (textWidth <= availableWidth && textHeight <= availableHeight) {
                    optimalSize = testSize;
                    minSize = testSize;
                } else {
                    maxSize = testSize;
                }
            }

            return Math.max(8, Math.floor(optimalSize));
        } catch (error) {
            console.log('Error calculating optimal font size', error);
            return 12;
        }
    };

    const chartOptions: Highcharts.Options = {
        chart: {
            type: 'solidgauge',
            height: height,
            width: width,
            backgroundColor: 'transparent',
            margin: [0, 0, 0, 0],
            reflow: true,
        },
        loading: {
            style: {
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '1.5rem',
                color: isThemeMode ? '#F8FAFC' : '#111827'
            },
        },
        title: {
            text: '',
        },
        pane: {
            center: ['50%', '50%'],
            size: '98%',
            startAngle: -135,
            endAngle: 135,
            background: [{
                backgroundColor: isThemeMode ? '#475569' : '#e5e7eb',
                innerRadius: '90%',
                outerRadius: '100%',
                borderWidth: 0,
                shape: 'arc'
            }]
        },
        exporting: {
            enabled: false
        },
        tooltip: {
            enabled: false
        },
        credits: {
            enabled: false
        },
        yAxis: {
            min: 0,
            max: 100,
            stops: [
                [0.1, getSpeedometerColor()],
                [0.5, getSpeedometerColor()],
                [0.9, getSpeedometerColor()]
            ],
            lineWidth: 0,
            tickWidth: 0,
            minorTickInterval: null,
            tickAmount: 2,
            title: {
                text: ''
            },
            labels: {
                enabled: false
            },
            plotBands: [{
                from: percentage - .5,
                to: percentage + 0,
                color: getSpeedometerColor(),
                innerRadius: '85%',
                outerRadius: '105%',
            }]
        },
        plotOptions: {
            solidgauge: {
                dataLabels: {
                    borderWidth: 0,
                    x: 0,
                    y: 0,
                    align: 'center',
                    verticalAlign: 'middle',
                    useHTML: false,
                    format: '{y}%',
                    style: {
                        color: getSpeedometerColor(),
                        fontWeight: 'bold',
                        fontSize: `${calculateOptimalFontSize()}px`,
                        textOutline: 'none' as any,
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        fontFamily: 'Arial, sans-serif', // Use consistent font for better width prediction
                    },
                    // Ensure text stays within bounds
                    crop: false,
                    overflow: 'justify',
                    // Add some padding to prevent edge touching
                    padding: 2,
                }
            }
        },
        series: [{
            name: label,
            type: 'solidgauge',
            data: [percentage],
            innerRadius: '90%',
            radius: '100%',
        }]
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '100%' }}>
            <Box sx={{ height: height, width: width }}>
                <HighchartsReact
                    ref={chartElementRef}
                    highcharts={Highcharts}
                    options={chartOptions}
                />
            </Box>
        </Box>
    );
};

const SpeedometerContainer: React.FC<{
    dayValue: string | number;
    intervalValue: string | number;
    type: 'sla' | 'abandoned';
    isDarkMode: boolean;
}> = ({ dayValue, intervalValue, type, isDarkMode }) => {

    const { elementRef, elementDimensions } = useElementDimensions({ debounceTime: 550 });
    const singleSpeedometerHeight = useMemo(
        () => (elementDimensions.height ? elementDimensions.height / 1 : 0),
        [elementDimensions.height]
    );
    const singleSpeedometerWidth = useMemo(
        () => (elementDimensions.width ? elementDimensions.width / 1.9 : 0),
        [elementDimensions.width]
    );
    const height = useMemo(
        () => (singleSpeedometerHeight < 110 ? 110 : singleSpeedometerHeight),
        [singleSpeedometerHeight]
    );
    const width = useMemo(
        () => (singleSpeedometerWidth < 110 ? 110 : singleSpeedometerWidth),
        [singleSpeedometerWidth]
    );



    return (
        <Box sx={{ display: 'flex', gap: .5, alignItems: 'center', height: '100%', width: '100%', justifyContent: 'space-around', }} ref={elementRef}>
            <Box sx={{ textAlign: 'center' }}>
                <Speedometer
                    percentage={parseFloat(dayValue?.toString() || '0')}
                    type={type}
                    label="DAY"
                    isThemeMode={isDarkMode}
                    height={height}
                    width={width}
                />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
                <Speedometer
                    percentage={parseFloat(intervalValue?.toString() || '0')}
                    type={type}
                    label="INTERVAL"
                    isThemeMode={isDarkMode}
                    height={height}
                    width={width}
                />
            </Box>
        </Box >
    );
};

const SpeedometerComboCard: React.FC<SpeedometerComboCardProps> = React.memo(({ dayValue, intervalValue, type, title, isDarkMode = false }) => {

    const isSla = useMemo(() => title === 'SLA %', [title]);

    return <Card elevation={0} sx={{
        backgroundColor: isDarkMode ? '#334155' : '#ffffff',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: isDarkMode ? 'none' : '1px solid #e5e7eb',
        padding: isSla ? "4px 8px" : "2px 8px",
        display: 'flex',
        flexDirection: 'column',
        borderRadius: isSla ? "16px 16px 0 0" : "0px 0px 16px 16px",
        minHeight: '60px',
        flex: 1,
        height: '49%',
    }}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-evenly' }}>
            <Typography className="kpi-title" sx={{
                color: isDarkMode ? '#E2E8F0' : '#1F2937',
                mb: 0.2,
                ml: 4,
                fontSize: 'clamp(1rem, 0.2174rem + 1.1594vw, 3rem)',
                fontWeight: 500,
                textAlign: 'center',
            }}>
                DAY
            </Typography>
            <Typography className="kpi-title" sx={{
                color: isDarkMode ? '#E2E8F0' : '#1F2937',
                mb: 0.2,
                fontSize: 'clamp(1rem, 0.2174rem + 1.1594vw, 3rem)',
                fontWeight: 500,
                textAlign: 'center',
            }}>
                {title}
            </Typography>
            <Typography className="kpi-title" sx={{
                color: isDarkMode ? '#E2E8F0' : '#1F2937',
                mb: 0.2,
                fontSize: 'clamp(1rem, 0.2174rem + 1.1594vw, 3rem)',
                fontWeight: 500,
                textAlign: 'center',
            }}>
                INTERVAL
            </Typography>
        </Box>
        <SpeedometerContainer
            dayValue={dayValue}
            intervalValue={intervalValue}
            type={type}
            isDarkMode={isDarkMode}
        />
    </Card >
}, (prevProps, nextProps) => {
    return (
        prevProps.dayValue === nextProps.dayValue &&
        prevProps.intervalValue === nextProps.intervalValue &&
        prevProps.type === nextProps.type &&
        prevProps.title === nextProps.title &&
        prevProps.isDarkMode === nextProps.isDarkMode
    );
});

const getSkillColor = (count: number, isThemeMode: boolean = false) => {
    if (count === 0) return isThemeMode ? '#ffffff' : '#000000'; // Normal color for 0
    if (count <= 2) return '#f59e0b'; // Amber for 1-2 calls (static)
    return '#ef4444'; // Red for ≥3 calls (with blink)
};

// Blinking logic
const shouldBlink = (type: string, value: number) => {
    switch (type) {
        case 'waitingCalls':
            return value > 2; // Blink when > 2
        case 'longCall':
            return value >= 2; // Blink when >= 2 (2-5 amber blink, >=6 red blink)
        case 'skill':
            return value >= 3; // Blink when >= 3
        default:
            return false;
    }
};

interface AgentDashboardMetricInterface {
    "AbandonedPercentagePerDay": number | string,
    "AbandonedPercentagePerInterval": number | string,
    "AHTPerDay": number | string,
    "AHTPerInterval": number | string,
    "SLAPercentagePerDay": number | string,
    "SLAPercentagePerInterval": number | string,
    "SLAPerDay": number | string,
    "SLAPerInterval": number | string,
    "TotalAbandonedPerDay": number | string,
    "TotalAbandonedPerInterval": number | string,
    "TotalCallsPerDay": number | string,
    "TotalCallsPerInterval": number | string,
}

interface AgentDataInterface {
    IdleCall: number | string,
    InBreak: number | string,
    LoggedIn: number | string,
    LongCall: number | string,
    OnCall: number | string,
    OnHold: number | string,
}



const DefaultDashboardMetric = {
    TotalCallsPerDay: '',
    SLAPerDay: '',
    SLAPercentagePerDay: '',
    TotalCallsPerInterval: '',
    SLAPerInterval: '',
    SLAPercentagePerInterval: '',
    AHTPerDay: '',
    TotalAbandonedPerDay: '',
    AbandonedPercentagePerDay: '',
    AHTPerInterval: '',
    TotalAbandonedPerInterval: '',
    AbandonedPercentagePerInterval: '',
}

const DefaultAgentData = {
    LoggedIn: '',
    InBreak: '',
    LongCall: '',
    OnCall: '',
    OnHold: '',
    IdleCall: '',
}

const DefaultEmptyDashboardMetric = {
    TotalCallsPerDay: '0',
    SLAPerDay: '0',
    SLAPercentagePerDay: '0',
    TotalCallsPerInterval: '0',
    SLAPerInterval: '0',
    SLAPercentagePerInterval: '0',
    AHTPerDay: '0',
    TotalAbandonedPerDay: '0',
    AbandonedPercentagePerDay: '0',
    AHTPerInterval: '0',
    TotalAbandonedPerInterval: '0',
    AbandonedPercentagePerInterval: '0',
}

const DefaultEmptyAgentData = {
    LoggedIn: '0',
    InBreak: '0',
    LongCall: '0',
    OnCall: '0',
    OnHold: '0',
    IdleCall: '0',
}


let isWidgetDataLoading = false;

const CallCenterWallboard: React.FC = (props: any) => {

    const fullscreenContainer = props?.fullscreenContainer
    const [lastFetchedTime, setLastFetchedTime] = useState<any>('');
    const [caseFilters, setCaseFilters] = useState<any>(props?.widgetData?.caseFilters || { caseCondition: 'AND', caseFilters: [{ condition: 'AND', conditionFilters: [{ filterType: '', filterSection: null, selectedFilters: [] }] }] });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [agentDashboardMetric, setAgentDashboardMetric] = useState<AgentDashboardMetricInterface>(DefaultDashboardMetric);
    const gridRef = useRef<HTMLDivElement | null>(null);
    const [agentData, setAgentData] = useState<AgentDataInterface>(DefaultAgentData);
    const [callsWaitingList, setCallsWaitingList] = useState<any>([]);
    const [callsWaitingCount, setCallsWaitingCount] = useState<number>(0);
    const { isDarkMode } = useThemeMode();

    function getMinColHeight() {
        try {
            return gridRef?.current ? `${Math.abs(gridRef.current.clientHeight - 140)}px` : '300px';
        } catch (error) {
            console.log("error in getMinColHeight", error);
            return '300px';
        }
    }

    const selectedCaseFiltersItems = useMemo(() => {
        let items: any = [];
        try {
            if (caseFilters?.caseFilters?.length > 0 && Array.isArray(caseFilters?.caseFilters)) {
                caseFilters.caseFilters.forEach((caseFilter: any) => {
                    if (caseFilter?.conditionFilters?.length > 0 && Array.isArray(caseFilter?.conditionFilters)) {
                        caseFilter?.conditionFilters?.forEach((condition: any) => {
                            if (condition?.selectedFilters && Array.isArray(condition?.selectedFilters)) {
                                let filterLabel = condition?.filterSection?.label;
                                condition?.selectedFilters?.forEach((filter: any) => {
                                    items.push(filterLabel + " - " + filter?.label);
                                });
                            }
                        });
                    }
                });
            }
            if (items?.length > 0) {
                items = [...new Set(items)];
            }
            return items;
        } catch (error) {
            console.log("error in selectedCaseFiltersItems", error)
            return [];
        }
    }, [caseFilters]);

    const caseFiltersLength = useMemo(() => {
        try {
            return selectedCaseFiltersItems?.length || 0;
        } catch (error) {
            console.log("error in caseFiltersLength", error)
            return 0;
        }
    }, [selectedCaseFiltersItems]);

    const caseFilterChipItems = useMemo(() => {
        return (selectedCaseFiltersItems?.length > 0 && Array.isArray(selectedCaseFiltersItems)) ? selectedCaseFiltersItems?.map((item: any, index: number) => (
            item ? <Chip key={index + item} label={item} color="primary" variant="outlined" size="small" /> : null
        )) : null;
    }, [selectedCaseFiltersItems]);


    const toggleDrawer = (isOpen: boolean) => {
        setIsDrawerOpen((isOpen));
    };

    function handleApplyCaseFilters(filters: any) {
        console.log("handleApplyCaseFilters filters", filters)
        setCaseFilters(filters);
        toggleDrawer(false);
        if (props && props?.handleSaveDataToFilter) {
            props?.handleSaveDataToFilter({
                widgetId: props?.widgetId,
                value: {
                    caseFilters: filters
                }
            })
        }
    }


    function transformCaseFiltersToApiParams(caseFiltersData: any) {
        try {
            if (!caseFiltersData || !caseFiltersData.caseFilters || !Array.isArray(caseFiltersData.caseFilters)) {
                return "0"; // Or return a default structure / throw error
            }

            const apiConditions = caseFiltersData.caseFilters.map((caseFilter: any, index: number) => {
                const conditionFilters = caseFilter?.conditionFilters?.map((condition: any) => {
                    // Ensure filter.id exists and is a string before parsing
                    let pFilterId = condition?.selectedFilters
                        ?.map((filter: any) => (filter && typeof filter.id === 'string' ? filter.id : NaN))
                    // .filter((id: number) => !isNaN(id));

                    // Ensure filterType exists and is a string before parsing
                    const pType = condition && typeof condition?.filterType === 'string' ? condition?.filterType : NaN;

                    // Only return if pType is a valid number and pFilterId is not empty
                    if (pFilterId && pFilterId?.length > 0) {
                        try {
                            if (Array.isArray(pFilterId)) {
                                pFilterId = pFilterId.map((item: any) => {
                                    if (item) {
                                        if (typeof item === 'string' && item.split(',').length > 1) {
                                            return item.split(',').map((item: any) => item);
                                        }
                                        return item;
                                    }
                                    return null;
                                }).filter((item: any) => item !== null).flat(3);
                            }
                        } catch (e) {
                            console.log("error in transformCaseFiltersToApiParams", e)
                        }
                        return {
                            pType: pType,
                            pFilterId: pFilterId,
                        };
                    }
                    return null; // Return null for invalid conditions
                }).filter((cf: any) => cf !== null); // Filter out null (invalid) conditions

                let condition: any = {};
                // if (conditionFilters?.length > 1) {
                condition.operator = caseFilter?.condition || "AND";
                // }
                return {
                    ...condition,
                    conditions: conditionFilters || [],
                    name: `case ${index + 1}`,
                };
            }).filter((c: any) => c?.conditions?.length > 0); // Ensure the case has valid conditions

            // The top-level operator seems to combine the different cases.
            // Based on the API example structure, it looks like the cases are OR'd together.
            // The caseFiltersData.caseCondition ("AND" in the example) applies *within* a case.
            if (apiConditions?.length === 0) {
                return "0";
            }
            let filter: any = {};
            // if (apiConditions?.length > 1) {
            //     filter.operator = caseFiltersData?.caseCondition || "AND";
            // }
            // if (apiConditions?.length === 1 && apiConditions[0]?.conditions?.length === 1) {
            //     return {
            //         filter: apiConditions[0]
            //     };
            // }
            return {
                filter: {
                    ...filter,
                    operator: caseFiltersData?.caseCondition,
                    conditions: apiConditions,
                },
            };
        } catch (e) {
            console.log("error in transformCaseFiltersToApiParams", e)
            return "0";
        }
    }

    async function getWidgetData(caseFilters: any) {
        // If already loading, don't start a new request
        if (isWidgetDataLoading) {
            console.log('Widget data fetch already in progress, skipping this cycle');
            return;
        }
        try {
            // Set loading flag to true
            isWidgetDataLoading = true;

            let filter: any = '0';
            try {
                if (caseFilters?.caseFilters?.length > 0 && Array.isArray(caseFilters?.caseFilters)) {
                    filter = transformCaseFiltersToApiParams(caseFilters);
                }
            } catch (e) {
                console.log("error in getWidgetData", e)
            }
            console.log("filter", structuredClone(filter))
            if (typeof filter === 'object') {
                filter = JSON.stringify(filter);
            }
            // Make all API calls in parallel
            await Promise.all([
                getWallboardMetric("Agent", filter),
                getWallboardMetric("Call", filter),
                getDashboardMetric(filter)
            ]);
            console.log('All widget data fetched successfully');
        } catch (error) {
            console.error('Error fetching widget data:', error);
        } finally {
            // Reset loading flag regardless of success or failure
            isWidgetDataLoading = false;
            setLastFetchedTime(getLastFetchedDetailFromBrowser());
        }
    };

    useEffect(() => {
        getWidgetData(caseFilters);
        const interval: any = setInterval(() => {
            getWidgetData(caseFilters);
        }, WIDGET_REFRESH_INTERVAL.CALL_CENTER_WALLBOARD);
        return () => clearInterval(interval);
    }, [caseFilters]);

    function convertDurationToSeconds(duration: string) {
        try {
            return duration.split(':').reduce((acc: any, curr: any, index: number) => acc + curr * (index === 0 ? 3600 : index === 1 ? 60 : 1), 0)
        } catch (error) {
            console.log("error in convertDurationToSeconds", error)
            return 0;
        }
    }

    async function getWallboardMetric(type: "Agent" | "Call", caseFilters: any) {
        try {
            let resp = await getWallboardCallMetric({ Type: type, Filter: caseFilters });
            if (resp && Array.isArray(resp)) {
                if (type === "Agent") {
                    const data = resp[0];
                    if (data && typeof data === 'object') {
                        setAgentData((prev: AgentDataInterface) => ({
                            ...prev,
                            LoggedIn: data.LoggedIn,
                            InBreak: data.InBreak,
                            LongCall: data.LongCall,
                            OnCall: data.OnCall,
                            OnHold: data.OnHold,
                            IdleCall: data.IdleCall
                        }))
                    } else {
                        setAgentData((prev: AgentDataInterface) => ({
                            ...prev,
                            ...DefaultEmptyAgentData
                        }))
                    }
                } else if (type === "Call") {
                    if (resp.length > 0) {
                        const skillNameCount: any = {};
                        let callQueueData: any[] = [];
                        resp.forEach((item: any) => {
                            try {
                                let pusH = true;
                                if (skillNameCount[item.SkillName]) {
                                    skillNameCount[item.SkillName] = skillNameCount[item.SkillName] + 1;
                                    let existingIndex = callQueueData.findIndex(i => i.skillName === item.SkillName);
                                    item.durationInSeconds = convertDurationToSeconds(item.Duration);
                                    if (existingIndex !== -1) {
                                        pusH = false;
                                        if (item.durationInSeconds > callQueueData[existingIndex].durationInSeconds) {
                                            callQueueData[existingIndex] = {
                                                skillName: item.SkillName,
                                                duration: item.Duration,
                                                durationInSeconds: item.durationInSeconds
                                            }
                                        }
                                    }
                                } else {
                                    skillNameCount[item.SkillName] = 1;
                                }
                                if (pusH) {
                                    callQueueData.push({
                                        skillName: item.SkillName,
                                        duration: item.Duration,
                                        durationInSeconds: convertDurationToSeconds(item.Duration)
                                    });
                                }
                            } catch (e) {
                                console.log("error in getWallboardMetric Call", e)
                            }
                        });
                        callQueueData = callQueueData.sort((a: any, b: any) => b.durationInSeconds - a.durationInSeconds);
                        callQueueData = callQueueData.map((item: any) => {
                            return {
                                ...item,
                                count: skillNameCount[item.skillName]
                            }
                        });
                        callQueueData = callQueueData.sort((a: any, b: any) => b.count - a.count);
                        let totalCount = 0;
                        callQueueData.forEach((item: any) => {
                            totalCount = totalCount + item.count;
                        });
                        setCallsWaitingCount(totalCount);
                        setCallsWaitingList(callQueueData);
                    } else {
                        setCallsWaitingList([]);
                        setCallsWaitingCount(0);
                    }
                }
            } else {
                if (type === "Agent") {
                    setAgentData((prev: AgentDataInterface) => ({
                        ...prev,
                        ...DefaultAgentData
                    }))
                } else {
                    setCallsWaitingList([]);
                    setCallsWaitingCount(0);
                }
            }
        } catch (error) {
            console.log("error in getWallboardMetric", error)
            if (type === "Agent") {
                setAgentData((prev: AgentDataInterface) => ({
                    ...prev,
                    ...DefaultAgentData
                }))
            } else {
                setCallsWaitingList([]);
                setCallsWaitingCount(0);
            }
        }
    }

    function convertSecondsToTime(seconds: string) {
        try {
            return secondsToMinSec(Number(Number(seconds).toFixed(0)))
        } catch (error) {
            console.log("error in convertSecondsToTime", error)
            return '';
        }
    }

    async function getDashboardMetric(caseFilters: any) {
        try {
            const resp = await getWallboardDashboardMetric({ CustomTagFilter: caseFilters });
            if (resp && Array.isArray(resp)) {
                const data = resp[0];
                if (data && typeof data === 'object') {
                    setAgentDashboardMetric((prev: any) => ({
                        ...prev,
                        TotalCallsPerDay: data.TotalCallsPerDay || '',
                        SLAPerDay: data.SLAPerDay || '',
                        SLAPercentagePerDay: data.SLAPercentagePerDay || '',
                        TotalCallsPerInterval: data.TotalCallsPerInterval || '',
                        SLAPerInterval: data.SLAPerInterval || '',
                        SLAPercentagePerInterval: data.SLAPercentagePerInterval || '',
                        AHTPerDay: data.AHTPerDay ? convertSecondsToTime(data.AHTPerDay) : '',
                        TotalAbandonedPerDay: data.TotalAbandonedPerDay || '',
                        AbandonedPercentagePerDay: data.AbandonedPercentagePerDay || '',
                        AHTPerInterval: data.AHTPerInterval ? convertSecondsToTime(data.AHTPerInterval) : '',
                        TotalAbandonedPerInterval: data.TotalAbandonedPerInterval || '',
                        AbandonedPercentagePerInterval: data.AbandonedPercentagePerInterval || '',
                    }))
                } else {
                    setAgentDashboardMetric((prev: any) => ({
                        ...prev,
                        ...DefaultEmptyDashboardMetric
                    }))
                }
            } else {
                setAgentDashboardMetric((prev: any) => ({
                    ...prev,
                    ...DefaultDashboardMetric
                }))
            }
        } catch (error) {
            console.log("error in getDashboardMetric", error)
            setAgentDashboardMetric((prev: any) => ({
                ...prev,
                ...DefaultDashboardMetric
            }))
        }
    }

    return (
        <Card className="customCard">
            <div className="card-header" style={{
                backgroundColor: isDarkMode ? '#1e293b' : '#FFFFFF',
                borderBottom: isDarkMode ? '1px solid rgba(10, 10, 10, 0.5)' : '1px solid #e5e7eb'
            }}>
                <div style={{
                    color: isDarkMode ? '#FFFFFF' : '#545454',
                    fontWeight: '500',
                    fontSize: '20px',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    Wallboard <div className="card-title-badge">Live</div>
                    <span className="last-fetched-time" style={{ marginLeft: '6px' }}>Last updated at {lastFetchedTime} </span>
                </div>
                <div className="card-actions">
                    <ThemeSwitcher />
                    {/* <WidgetDownload element={downloadContent} handleDownload={handleDownload} xlsx rowData={getRowData(rowData)} columns={columnData} name={'Queue Counter'}></WidgetDownload> */}
                    <div className="card-icon" onClick={() => toggleDrawer(true)}>
                        <Badge badgeContent={caseFiltersLength} color="primary">
                            <FilterAltOutlinedIcon fontSize="small" style={{ color: '#536580' }} />
                        </Badge>
                    </div>
                </div>
            </div>

            <FilterDrawerCaseWise
                isOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                applyFilters={handleApplyCaseFilters}
                existingCaseFilters={caseFilters}
                allowedFilters={[]}
                dynamicFilters={true}
                useTagFilters={true}
                fullscreenContainer={fullscreenContainer}
            />
            <Box className={`wallboard-container-call-center`} sx={{ background: isDarkMode ? '#1e293b' : '#FFFFFF', }}>
                {caseFiltersLength > 0 &&
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        gap: '4px',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        px: 2,
                        py: 1,
                    }}>
                        {caseFilterChipItems}
                    </Box>
                }
                {/* Main Dashboard Grid */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    // gap: 2,
                    px: 2,
                    // py: 1,
                    pb: 2,
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                }}>
                    <Box sx={{ display: 'flex', width: '100%', height: '105%', overflow: 'auto', }}>
                        {/* Combined Cards with Table*/}
                        <Grid container spacing={1} sx={{ height: '100%', }} my={0} py={0}>
                            {/* First Column */}
                            <Grid item sm={12} md={6} lg={2} sx={{ height: { xs: 'auto', sm: 'auto', md: 'auto', lg: '100%' } }}>
                                <Grid container spacing={1} sx={{ height: '100%' }} ref={gridRef}>
                                    <Grid item xs={12}>
                                        <KPICard
                                            title="TOTAL CALLS"
                                            dayValue={agentDashboardMetric.TotalCallsPerDay}
                                            intervalValue={agentDashboardMetric.TotalCallsPerInterval}
                                            backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                            comboCard={true}
                                            isThemeMode={isDarkMode}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <KPICard
                                            title="AHT"
                                            dayValue={agentDashboardMetric.AHTPerDay}
                                            intervalValue={agentDashboardMetric.AHTPerInterval}
                                            backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                            comboCard={true}
                                            isThemeMode={isDarkMode}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <KPICard
                                            title="LONG CALL"
                                            value={String(agentData.LongCall || 0).padStart(2, '0')}
                                            backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                            shouldBlink={shouldBlink('longCall', Number(agentData.LongCall || 0))}
                                            isThemeMode={isDarkMode}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            {/* Second Column */}
                            <Grid item sm={12} md={6} lg={2} sx={{ height: { xs: 'auto', sm: 'auto', md: 'auto', lg: '100%' } }}>
                                <Grid container spacing={1} sx={{ height: '100%' }}>
                                    <Grid item xs={12}>
                                        <KPICard
                                            title="SLA - 20 Sec"
                                            dayValue={agentDashboardMetric.SLAPerDay}
                                            intervalValue={agentDashboardMetric.SLAPerInterval}
                                            backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                            comboCard={true}
                                            isThemeMode={isDarkMode}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <KPICard
                                            title="Abandoned"
                                            dayValue={agentDashboardMetric.TotalAbandonedPerDay}
                                            intervalValue={agentDashboardMetric.TotalAbandonedPerInterval}
                                            backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                            comboCard={true}
                                            isThemeMode={isDarkMode}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <KPICard
                                            title="Calls Waiting"
                                            value={callsWaitingCount || 0}
                                            backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                            shouldBlink={shouldBlink('waitingCalls', callsWaitingCount || 0)}
                                            isThemeMode={isDarkMode}
                                        />
                                    </Grid>

                                </Grid>
                            </Grid>
                            {/* Third Column */}
                            <Grid item sm={12} md={12} lg={5} sx={{ height: { lg: '100%', md: 'auto', sm: 'auto', xs: 'auto' }, }} >
                                {/* <Grid container spacing={1} sx={{ height: '100%', minHeight: getMinColHeight() }}> */}
                                {/* <Grid item xs={12} sx={{ height: { lg: 'auto', md: '300px', sm: '300px', xs: '300px' }, }} > */}
                                <SpeedometerComboCard
                                    title="SLA %"
                                    dayValue={agentDashboardMetric.SLAPercentagePerDay}
                                    intervalValue={agentDashboardMetric.SLAPercentagePerInterval}
                                    type="sla"
                                    isDarkMode={isDarkMode}
                                />
                                {/* </Grid> */}
                                {/* <Grid item xs={12} sx={{ height: { lg: 'auto', md: '300px', sm: '300px', xs: '300px' }, }} > */}
                                <SpeedometerComboCard
                                    title="Abandoned %"
                                    dayValue={agentDashboardMetric.AbandonedPercentagePerDay}
                                    intervalValue={agentDashboardMetric.AbandonedPercentagePerInterval}
                                    type="abandoned"
                                    isDarkMode={isDarkMode}
                                />
                                {/* </Grid> */}
                                {/* </Grid> */}
                            </Grid>
                            {/* Fourth Column Table */}
                            <Grid item sm={12} md={12} lg={3} sx={{ height: { lg: '100%', md: 'auto', sm: 'auto', xs: 'auto' } }}>
                                <Grid container spacing={1} sx={{ height: '100%' }}>
                                    <Grid item sm={12} sx={{ height: '100%' }}>
                                        <Card sx={{
                                            backgroundColor: isDarkMode ? '#334155' : '#ffffff',
                                            height: { lg: '100%', md: 'auto', sm: 'auto', xs: 'auto' },
                                            display: 'flex',
                                            boxShadow: isDarkMode ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                                            flexDirection: 'column',
                                            border: isDarkMode ? 'none' : '1px solid #e5e7eb',
                                            borderRadius: '16px',
                                            minHeight: '150px'
                                        }}>
                                            <Typography sx={{
                                                color: isDarkMode ? '#E2E8F0' : '#1F2937',
                                                fontSize: 'clamp(1.3rem, -1rem + 2vw, 2rem)',
                                                fontWeight: 600,
                                                textAlign: 'center',
                                                p: .5,
                                                borderBottom: isDarkMode ? '1px solid #475569' : '1px solid #e5e7eb'
                                            }}>
                                                CALLS WAITING
                                            </Typography>

                                            <Box sx={{
                                                display: 'flex',
                                                p: .5,
                                                borderBottom: isDarkMode ? '1px solid #475569' : '1px solid #e5e7eb',
                                                backgroundColor: isDarkMode ? '#475569' : '#f8fafc',
                                                width: '100%',
                                            }}>
                                                <Typography sx={{
                                                    flex: 1,
                                                    fontSize: 'clamp(1.2rem, -1rem + 2vw, 2rem)',
                                                    fontWeight: 600,
                                                    color: isDarkMode ? '#d1d5db' : '#374151',
                                                    textAlign: 'left',
                                                    pl: .5,
                                                    whiteSpace: 'normal',
                                                    overflowWrap: 'anywhere',
                                                    wordBreak: 'break-word',
                                                    lineHeight: 1.3,
                                                    width: '32%'
                                                }}>
                                                    SKILL
                                                </Typography>
                                                <Typography sx={{
                                                    minWidth: '80px',
                                                    fontSize: 'clamp(1.2rem, -1rem + 2vw, 2rem)',
                                                    fontWeight: 600,
                                                    color: isDarkMode ? '#d1d5db' : '#374151',
                                                    textAlign: 'center',
                                                    whiteSpace: 'normal',
                                                    overflowWrap: 'anywhere',
                                                    wordBreak: 'break-word',
                                                    lineHeight: 1.3,
                                                    width: '32%'
                                                }}>
                                                    COUNT
                                                </Typography>
                                                <Typography sx={{
                                                    minWidth: '100px',
                                                    fontSize: 'clamp(1.2rem, -1rem + 2vw, 2rem)',
                                                    fontWeight: 600,
                                                    color: isDarkMode ? '#d1d5db' : '#374151',
                                                    textAlign: 'center',
                                                    mx: 1,
                                                    whiteSpace: 'normal',
                                                    // overflowWrap: 'anywhere',
                                                    // wordBreak: 'break-word',
                                                    lineHeight: 1.3,
                                                    width: '33%',
                                                }}>
                                                    DURATION
                                                </Typography>
                                            </Box>

                                            <Box sx={{ flex: 1, overflow: 'auto', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
                                                {callsWaitingList.map((skill: any, index: number) => {
                                                    const skillColor = getSkillColor(skill.count, isDarkMode);
                                                    const shouldBlinkSkill = shouldBlink('skill', skill.count);

                                                    return (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                p: 1,
                                                                borderBottom: isDarkMode ? '1px solid #475569' : '1px solid #e5e7eb',
                                                                width: '100%',
                                                                '&:nth-of-type(even)': {
                                                                    backgroundColor: isDarkMode ? '#3f4b5c' : '#f9fafb'
                                                                }
                                                            }}
                                                        >
                                                            <Typography sx={{
                                                                flex: 1,
                                                                fontSize: 'clamp(1.3rem, -1rem + 2vw, 2rem)',
                                                                color: isDarkMode ? '#ffffff' : '#374151',
                                                                textAlign: 'left',
                                                                pl: .2,
                                                                whiteSpace: 'normal',
                                                                overflowWrap: 'anywhere',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.35,
                                                                width: '32%'
                                                            }}>
                                                                {skill.skillName}
                                                            </Typography>
                                                            <Typography className={shouldBlinkSkill ? 'blinking' : ''} sx={{
                                                                minWidth: '80px',
                                                                fontSize: 'clamp(1.3rem, -1rem + 2vw, 2rem)',
                                                                fontWeight: 'bold',
                                                                color: skillColor,
                                                                textAlign: 'center',
                                                                whiteSpace: 'normal',
                                                                overflowWrap: 'anywhere',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.35,
                                                                width: '32%'
                                                            }}>
                                                                {skill.count}
                                                            </Typography>
                                                            <Typography sx={{
                                                                minWidth: '100px',
                                                                fontSize: 'clamp(1.3rem, -1rem + 2vw, 2rem)',
                                                                color: isDarkMode ? '#94a3b8' : '#6b7280',
                                                                textAlign: 'center',
                                                                whiteSpace: 'normal',
                                                                overflowWrap: 'anywhere',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.35,
                                                                width: '32%'
                                                            }}>
                                                                {skill.duration}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Agent Status Row */}
                    <Box sx={{ display: 'flex', width: '100%', mt: { lg: "-8px", md: 0 } }}>
                        <Grid container spacing={1} mt={0} pt={{ xs: 1, sm: 1, md: 1, lg: 0 }}>
                            <Grid item xs={12} sm={6} md={6} lg={2.4}>
                                <KPICard
                                    title="LOGGED IN"
                                    value={agentData.LoggedIn}
                                    backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                    isThemeMode={isDarkMode}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={2.4}>
                                <KPICard
                                    title="ON CALL"
                                    value={agentData.OnCall}
                                    backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                    isThemeMode={isDarkMode}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={2.4}>
                                <KPICard
                                    title="IN BREAK"
                                    value={agentData.InBreak}
                                    backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                    isThemeMode={isDarkMode}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={2.4}>
                                <KPICard
                                    title="ON HOLD"
                                    value={agentData.OnHold}
                                    backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                    isThemeMode={isDarkMode}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={2.4}>
                                <KPICard
                                    title="IDLE AGENT"
                                    value={agentData.IdleCall}
                                    backgroundColor={isDarkMode ? '#334155' : '#ffffff'}
                                    isThemeMode={isDarkMode}
                                />
                            </Grid>
                        </Grid>

                    </Box>
                </Box >

            </Box >
        </Card >
    );
};

export default CallCenterWallboard; 